<script setup>
import { ref, computed } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { platformLabel as platformName } from '@/lib/platformLabel'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import PublicGallery from '@/Components/Public/PublicGallery.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import BookingRequestForm from '@/Components/Public/BookingRequestForm.vue'
import DateField from '@/Components/Public/DateField.vue'

// Charter detail: the boat, its formats and whole-boat prices, and a
// request-to-book card (date, format, guests). Every charter is bespoke —
// there is no instant checkout; the request lands in the CRM queue with the
// format so the estimate resolves per whole vessel (PriceQuote#quote_charter).
defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  charter:      { type: Object, required: true },
  trip_formats: { type: Array,  default: () => [] },
  base_path:    { type: String, default: '/charters' }
})

const c = props.charter
const photos = c.photos?.length ? c.photos : []
const today = computed(() => new Date().toLocaleDateString('en-CA'))

// The request's trip_format mirrors the boat's priced formats; "custom"
// stays available so a customer can ask for something the boat does not
// list. Overnight maps to the request's multi_day (nights from the dates).
const FORMAT_TO_TRIP = { full_day: 'full_day', half_day: 'half_day', sunset: 'sunset', overnight: 'multi_day' }
const formatOptions = computed(() => {
  const listed = (c.formats || []).map(f => ({ key: f.format, trip: FORMAT_TO_TRIP[f.format], price: f.display_price, hours: f.hours, perNight: f.per_night }))
  return [...listed, { key: 'custom', trip: 'custom', price: null }]
})
const requestFormat = ref(formatOptions.value[0]?.key || 'custom')
const requestDate   = ref('')
const requestEnd    = ref('')
const requestGuests = ref(2)
const isOvernight   = computed(() => requestFormat.value === 'overnight')
const details = computed(() => ({
  start_on: requestDate.value,
  end_on: isOvernight.value ? requestEnd.value : '',
  adults: requestGuests.value,
  trip_format: FORMAT_TO_TRIP[requestFormat.value] || 'custom'
}))
const canRequest = computed(() => !!requestDate.value && (!isOvernight.value || !!requestEnd.value))

const eyebrow = computed(() => {
  const parts = [t(`public.charters.vessel_types.${c.vessel_type}`)]
  if (c.length_ft) parts.push(t('public.charters.ft', { n: c.length_ft }))
  parts.push(t('public.charters.up_to', { n: c.capacity }))
  return parts.join(' · ')
})

const chatCtas = computed(() => Object.entries(c.contact_intents || {}).map(([platform, url]) => ({ platform, url, name: platformName(platform) })))

const specs = computed(() => [
  ['capacity',       t('public.charters.up_to', { n: c.capacity })],
  ['length',         c.length_ft ? t('public.charters.ft', { n: c.length_ft }) : null],
  ['cabins',         c.cabins != null ? String(c.cabins) : null],
  ['crew',           c.crew != null ? String(c.crew) : null],
  ['year_built',     c.year_built ? String(c.year_built) : null],
  ['home_marina',    c.home_marina ? t(`public.charters.marinas.${c.home_marina}`) : null],
  ['crew_languages', c.crew_languages?.length ? c.crew_languages.map(l => t(`public.experiences.guide_languages.${l}`)).join(', ') : null],
].filter(([, v]) => v))

const listSections = computed(() => ['highlights', 'inclusions', 'not_included', 'itineraries'].filter(k => (c[k] || []).length))
</script>

<template>
  <Head>
    <title>{{ charter.title }} · {{ t('public.charters.head_title') }}</title>
    <meta name="description" :content="charter.summary || charter.description">
  </Head>

  <BrandHero kind="charter" :eyebrow="eyebrow" :title="charter.title">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.charters.back') }}</a>
    </template>
    <p v-if="charter.summary" class="bhero-lede">{{ charter.summary }}</p>
    <span v-if="charter.luxury" class="bhero-vip">{{ t('public.vip.badge') }}</span>
    <span v-else-if="charter.tier === 'premium'" class="bhero-vip bhero-vip--premium">{{ t('public.tiers.premium') }}</span>
    <p class="bhero-note">{{ t('public.charters.route_note') }}</p>
    <div v-if="chatCtas.length" class="bhero-ctas">
      <LuxButton v-for="(cta, idx) in chatCtas" :key="cta.platform" :href="cta.url" :variant="idx === 0 ? 'solid' : 'line'" tone="dark" :glyph="cta.platform" target="_blank" rel="noopener">
        {{ t('public.write_on', { platform: cta.name }) }}
      </LuxButton>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <PublicGallery :photos="photos" :alt="charter.title">
        <template #placeholder>
          <CatalogPlaceholder kind="charter" />
        </template>
      </PublicGallery>

      <div class="detail">
        <div class="detail__content">
          <section v-if="charter.description" class="card">
            <h2 class="section-title">{{ t('public.charters.sections.about') }}</h2>
            <p class="prose">{{ charter.description }}</p>
          </section>

          <section class="card">
            <h2 class="section-title">{{ t('public.charters.sections.pricing') }}</h2>
            <table class="formats">
              <tbody>
                <tr v-for="f in charter.formats" :key="f.format">
                  <th scope="row">{{ t(`public.charters.formats.${f.format}`) }}</th>
                  <td class="formats__hours mono">
                    <template v-if="f.per_night">{{ t('public.charters.per_night') }}</template>
                    <template v-else-if="f.hours">{{ t('public.charters.hours', { n: f.hours }) }}</template>
                  </td>
                  <td class="formats__price fw-value">{{ charter.price_on_request ? t('public.vip.price_on_request') : f.display_price }}</td>
                </tr>
              </tbody>
            </table>
            <p class="hint">{{ t('public.charters.per_boat_note') }}</p>
          </section>

          <section v-if="charter.features?.length" class="card">
            <h2 class="section-title">{{ t('public.charters.sections.features') }}</h2>
            <ul class="occ occ--features">
              <li v-for="f in charter.features" :key="f">{{ t(`public.charters.features.items.${f}`) }}</li>
            </ul>
          </section>

          <section v-for="k in listSections" :key="k" class="card">
            <h2 class="section-title">{{ t(`public.charters.sections.${k}`) }}</h2>
            <ul class="bullets">
              <li v-for="(item, i) in charter[k]" :key="i">{{ item }}</li>
            </ul>
          </section>
        </div>

        <aside class="detail__aside">
          <div class="facts">
            <h2 class="section-title">{{ t('public.charters.sections.specs') }}</h2>
            <dl class="specs">
              <template v-for="[k, v] in specs" :key="k">
                <dt>{{ t(`public.charters.fields.${k}`) }}</dt>
                <dd class="fw-value">{{ v }}</dd>
              </template>
            </dl>
            <ul v-if="charter.occasions?.length" class="occ">
              <li v-for="o in charter.occasions" :key="o">{{ t(`public.charters.occasions.${o}`) }}</li>
            </ul>
          </div>

          <div id="request-to-book" class="plan-card">
            <h2 class="section-title">{{ t('public.charters.booking.title') }}</h2>
            <p class="plan-note">{{ t('public.charters.booking.note') }}</p>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.charters.booking.format') }}</span>
              <select v-model="requestFormat" class="plan-input">
                <option v-for="f in formatOptions" :key="f.key" :value="f.key">
                  {{ t(`public.charters.formats.${f.key}`) }}<template v-if="f.price && !charter.price_on_request"> · {{ f.price }}</template>
                </option>
              </select>
            </label>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.charters.booking.date') }}</span>
              <DateField v-model="requestDate" :min="today" class="plan-input" :placeholder="t('public.pick_date')" />
            </label>
            <label v-if="isOvernight" class="plan-row">
              <span class="plan-label">{{ t('public.charters.booking.end_date') }}</span>
              <DateField v-model="requestEnd" :min="requestDate || today" class="plan-input" :placeholder="t('public.pick_date')" />
            </label>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.charters.booking.guests') }}</span>
              <input v-model.number="requestGuests" type="number" min="1" :max="charter.capacity" class="plan-input plan-input--short">
            </label>
            <h3 class="plan-subtitle">{{ t('public.booking_request.title') }}</h3>
            <BookingRequestForm
              bookable-type="charter"
              :bookable-id="charter.id"
              :details="details"
              :disabled="!canRequest"
            />
            <p v-if="chatCtas.length" class="plan-divider">{{ t('public.booking_request.or_chat') }}</p>
            <div v-if="chatCtas.length" class="plan-ctas">
              <LuxButton v-for="cta in chatCtas" :key="cta.platform" :href="cta.url" variant="line" tone="light" :glyph="cta.platform" block target="_blank" rel="noopener">
                {{ t('public.charters.booking.cta_via', { platform: cta.name }) }}
              </LuxButton>
            </div>
          </div>

          <div class="trust-card">
            <h2 class="section-title">{{ t('public.charters.trust.title') }}</h2>
            <ul class="trust-list">
              <li>{{ t('public.charters.trust.licensed') }}</li>
              <li>{{ t('public.charters.trust.deposit') }}</li>
              <li>{{ t('public.charters.trust.local') }}</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 52ch; opacity: 0.94; }
.bhero-vip { display: inline-flex; align-items: center; margin: 1rem 0.4rem 0 0; padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700; background: var(--ink); color: var(--gold, var(--warn)); border: 1px solid var(--gold, var(--warn)); }
.bhero-vip--premium { color: var(--sky, #E4F1F7); border-color: var(--sky, #E4F1F7); }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.detail { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-top: 1.5rem; }
@media (min-width: 880px) { .detail { grid-template-columns: 1.6fr 0.9fr; gap: 2rem; align-items: start; } }
.detail__content { display: grid; gap: 1rem; }
@media (min-width: 880px) { .detail__aside { position: sticky; top: 1.5rem; } }
.card, .facts, .plan-card, .trust-card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.plan-card, .trust-card { margin-top: 1.25rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.hint { margin: 0.75rem 0 0; font-size: 0.85rem; color: var(--muted); }
.mono { font-family: var(--font-mono, var(--mono)); font-size: 0.8rem; letter-spacing: 0.04em; text-transform: uppercase; color: var(--muted); }

.formats { width: 100%; border-collapse: collapse; }
.formats th, .formats td { padding: 0.7rem 0.5rem; text-align: left; border-bottom: 1px solid var(--line); }
.formats tbody th { color: var(--ink); font-weight: 600; font-family: var(--font-display, var(--display)); font-size: 1.25rem; }
.formats__price { text-align: right; font-family: var(--font-display, var(--display)); font-size: 1.35rem; color: var(--ink); }
.formats tbody tr:last-child th, .formats tbody tr:last-child td { border-bottom: none; }

.bullets { margin: 0; padding: 0; list-style: none; display: grid; gap: 0.5rem; }
.bullets li { position: relative; padding-left: 1.4rem; color: var(--inkSoft); line-height: 1.5; }
.bullets li::before { content: ''; position: absolute; left: 0.2rem; top: 0.6rem; width: 0.45rem; height: 0.45rem; border-radius: 50%; background: var(--second); }

.specs { display: grid; grid-template-columns: auto 1fr; gap: 0.5rem 1rem; margin: 0; }
.specs dt { color: var(--muted); font-size: 0.85rem; }
.specs dd { margin: 0; color: var(--ink); }
.occ { list-style: none; margin: 1rem 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 0.4rem; }
.occ li { padding: 0.3rem 0.7rem; border-radius: 999px; background: var(--sky, var(--secondSoft)); color: var(--ink); font-size: 0.8rem; font-weight: 600; }
.occ--features { margin-top: 0; }

.plan-note { margin: 0 0 0.85rem; color: var(--inkSoft); font-size: 0.9rem; line-height: 1.5; }
.plan-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 0.7rem; }
.plan-label { color: var(--inkSoft); font-size: 0.95rem; }
.plan-input { border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem); background: var(--bg); color: var(--ink); padding: 0.45rem 0.6rem; font: inherit; max-width: 60%; color-scheme: light; }
.plan-input--short { max-width: 6rem; }
.plan-subtitle { margin: 1.1rem 0 0; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); }
.plan-divider { margin: 0.9rem 0 0; text-align: center; font-size: 0.8rem; color: var(--muted); }
.plan-ctas { margin-top: 1rem; display: grid; gap: 0.5rem; }

.trust-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.6rem; }
.trust-list li { position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.4; }
.trust-list li::before { content: "✓"; position: absolute; left: 0; top: 0; color: var(--ok); font-weight: 700; }
.bhero-note { margin: 0.9rem 0 0; font-size: 0.95rem; line-height: 1.5; opacity: 0.92; max-width: 46ch; }
</style>
